import { getExerciseCover } from 'app/utils/utils';
import { removeHtmlTagsFromString, successResponse } from 'core';
import { getQuestions } from 'core/crud/tests.crud';
import { useCourse } from 'core/lite/hooks/useCourse';
import { useCourseItems } from 'core/lite/hooks/useCourseItems';
import * as coreEntities from 'core/store/index';
import { questionIsAutoevaluative } from 'core/utils/autoevaluative';
import { getLemonadeType } from 'core/utils/lemonade';
import { flattenDepth } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const ACTIVITY_SOURCE = { ALL: 'all', BOOK: 'book', ADDITIONAL: 'additional' };
export const QUESTION_AUTHOR = { ALL: 'all', EDITORIAL: 'editorial', TEACHER: 'teacher' };

const FILTER_QUESTIONS_INITIAL_STATE = {
  author: QUESTION_AUTHOR.ALL,
  type: 'all',
  source: ACTIVITY_SOURCE.ALL,
  isAutoevaluative: false,
  learningObjective: [],
  unit: [],
  lesson: [],
  educationLevel: [],
  educationYear: [],
  discipline: [],
};
const PAGE_SIZE = 50;

export default function useDialogSearchQuestionsNew({ open, onAdd, onCancel, alreadyAddedQuestions, questionType, courseGuid }) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [questions, setQuestions] = useState({
    left: 0,
    offset: 0,
    pageSize: PAGE_SIZE,
    data: [],
    total: 0,
    discardedCount: 0,
    hasMore: false,
    loading: false,
  });
  const [questionFilters, setQuestionFilters] = useState(
    questionType
      ? {
          ...FILTER_QUESTIONS_INITIAL_STATE,
          type: [questionType.map((type) => type.guid)],
          isAutoevaluative: true,
        }
      : FILTER_QUESTIONS_INITIAL_STATE
  );
  const [filtersEmpty, setFiltersEmpty] = useState(true);
  const [questionsSearch, setQuestionsSearch] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [selectedPage, setSelectedPage] = useState(1);
  const [learningObjectives, setLearningObjectives] = useState([]);
  const [selectedLearningObjectives, setSelectedLearningObjectives] = useState([]);
  const [courseTabActive, setCourseTabActive] = useState('thisCourse');
  const [currentCourseItems, setCurrentCourseItems] = useState([]);
  const [edLevels, setEdLevels] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);

  const { data: course } = useCourse(courseGuid);
  const { courseItems } = useCourseItems(courseGuid);
  const listOfAllLearningObjectives = useSelector((state) => coreEntities.learningObjectives.selectors.getLearningObjectives(state));
  const educationLevels = useSelector((state) => coreEntities.educationLevels.selectors.getEducationLevels(state));
  const disciplines = useSelector((state) => coreEntities.educationLevels.selectors.getDisciplines(state));
  const recentQuestionSearchs = useSelector((state) => coreEntities.history.selectors.getRecentQuestionSearchs(state));

  useEffect(() => {
    setRecentSearches(recentQuestionSearchs);
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedPage(1);
      onGetQuestions(1);
    }
  }, [open, questionFilters, questionsSearch, alreadyAddedQuestions]);

  useEffect(() => {
    setTotalPages(Math.ceil(questions.total / PAGE_SIZE));
  }, [questions?.total]);

  useEffect(() => {
    if (questionFilters !== FILTER_QUESTIONS_INITIAL_STATE) setFiltersEmpty(false);
  }, [questionFilters]);

  useEffect(() => {
    if (course && courseItems.length > 0) {
      const parsedCourse = parseCourse(course, courseItems);
      setCurrentCourseItems(parsedCourse);
      selectAllUnits(parsedCourse);
    }
  }, [courseItems, course]);

  useEffect(() => {
    setLearningObjectives(parseLoToSelectOptions(listOfAllLearningObjectives));
  }, [listOfAllLearningObjectives]);

  useEffect(() => {
    if (educationLevels.length > 0) {
      setEdLevels(educationLevels);
    }
  }, [edLevels]);

  function buildQuestionTypesFilter() {
    let questionTypes;
    if (questionFilters.type !== 'all') {
      questionTypes = questionFilters.type.length > 0 ? flattenDepth(questionFilters.type, 1) : questionFilters.type;
    }
    if (questionFilters.type.length === 0 && questionType) questionTypes = questionType.map((type) => type.guid);
    return questionTypes;
  }

  function buildQuestionFilters() {
    const filters = {};
    if (questionsSearch.length > 2) filters.search = questionsSearch;
    if (questionFilters.author !== QUESTION_AUTHOR.ALL) filters.author = questionFilters.author;
    if (questionFilters.source !== ACTIVITY_SOURCE.ALL && questionFilters.author !== QUESTION_AUTHOR.TEACHER) {
      filters.source = questionFilters.source;
    }
    const questionTypes = buildQuestionTypesFilter();
    if (questionTypes) filters.questionTypes = questionTypes;
    if (questionFilters.isAutoevaluative) filters.isAutoevaluative = 1;
    if (questionFilters.learningObjective.length > 0) filters.learningObjective = questionFilters.learningObjective;
    if (questionFilters.unit) filters.unit = questionFilters.unit;
    if (questionFilters.lesson) filters.lesson = questionFilters.lesson;
    if (questionFilters.educationLevel) filters.educationLevel = questionFilters.educationLevel;
    if (questionFilters.educationYear) filters.educationYear = questionFilters.educationYear;
    if (questionFilters.discipline) filters.discipline = questionFilters.discipline;
    return filters;
  }

  async function onGetQuestions(currentPage) {
    setIsLoading(true);
    const page = currentPage - 1;
    const offset = PAGE_SIZE * page;

    if (questionsSearch.length > 2) onAddQuestionSearch(questionsSearch);
    const params = {
      pageSize: PAGE_SIZE,
      offset: offset,
      filters: buildQuestionFilters(),
    };

    const questionsResponse = await getQuestions(params);
    if (successResponse(questionsResponse)) parseQuestions(questionsResponse, offset);
    setIsLoading(false);
  }

  function onPageChange(page) {
    setSelectedPage(page);
    onGetQuestions(page);
  }

  function onSelectFilter(item, type, state) {
    let filters = { ...questionFilters };
    let isParent = type === 'unit' || type === 'educationLevel';
    if (state === true) {
      filters = isParent ? checkAllChildren(item, type) : checkParent(item, type) || filters;
      filters = { ...filters, [type]: [...questionFilters[type], item] };
    } else {
      filters = isParent ? uncheckAllChildren(item, type) : uncheckParent(item, type) || filters;
      filters = { ...filters, [type]: questionFilters[type].filter((el) => el !== item) };
      if (filters.unit.length === 0 && filters.lesson.length === 0) return;
    }
    setQuestionFilters({ ...filters });
  }

  function uncheckParent(item, type) {
    if (type === 'lesson') {
      const parentUnit = currentCourseItems.units.filter((unit) => unit.lessons.some((lesson) => lesson.lessonGuid === item))[0];
      return { ...questionFilters, unit: questionFilters.unit.filter((unit) => unit !== parentUnit.unitGuid) };
    }

    if (type === 'educationYear') {
      const educationLevel = edLevels.filter((level) => level.educationYears.some((year) => year.education_year_guid === item))[0];
      return { ...questionFilters, educationLevel: questionFilters.educationLevel.filter((level) => level !== educationLevel.guid) };
    }

    return { ...questionFilters };
  }

  function checkAllChildren(item, type) {
    if (type === 'unit') {
      let unit = currentCourseItems.units.filter((unit) => unit.unitGuid === item)[0];
      let lessons = unit.lessons.map((lesson) => lesson.lessonGuid);
      return { ...questionFilters, lesson: [...questionFilters.lesson, ...lessons] };
    }

    if (type === 'educationLevel') {
      let educationLevel = edLevels.filter((level) => level.guid === item)[0];
      let educationYears = educationLevel.educationYears.map((year) => year.education_year_guid);
      return { ...questionFilters, educationYear: [...questionFilters.educationYear, ...educationYears] };
    }

    return { ...questionFilters };
  }

  function uncheckAllChildren(item, type) {
    if (type === 'unit') {
      let unit = currentCourseItems.units.filter((unit) => unit.unitGuid === item)[0];
      let lessons = unit.lessons.map((lesson) => lesson.lessonGuid);
      return { ...questionFilters, lesson: questionFilters.lesson.filter((lesson) => !lessons.includes(lesson)) };
    }

    if (type === 'educationLevel') {
      let educationLevel = edLevels.filter((level) => level.guid === item)[0];
      let educationYears = educationLevel.educationYears.map((year) => year.education_year_guid);
      return { ...questionFilters, educationYear: questionFilters.educationYear.filter((year) => !educationYears.includes(year)) };
    }
    return { ...questionFilters };
  }

  function checkParent(item, type) {
    if (type === 'lesson') {
      const unit = currentCourseItems.units.filter((unit) => unit.lessons.some((lesson) => lesson.lessonGuid === item))[0];
      const lessonsOfUnit = unit.lessons.map((lesson) => lesson.lessonGuid);
      if (lessonsOfUnit.every((lesson) => questionFilters.lesson.concat(item).includes(lesson)))
        return { ...questionFilters, unit: [...questionFilters.unit, unit.unitGuid] };
    }

    if (type === 'educationYear') {
      const educationLevel = edLevels.filter((level) => level.educationYears.some((year) => year.education_year_guid === item))[0];
      const educationYears = educationLevel.educationYears.map((year) => year.education_year_guid);
      if (educationYears.every((year) => questionFilters.educationYear.concat(item).includes(year))) {
        return { ...questionFilters, educationLevel: [...questionFilters.educationLevel, educationLevel.guid] };
      }
    }
    return { ...questionFilters };
  }

  function selectAllUnits(parsedCourse) {
    if (!parsedCourse?.units?.length) return;
    const allUnitGuids = parsedCourse.units.map((unit) => unit.unitGuid);
    const allLessonGuids = parsedCourse.units.flatMap((unit) => unit.lessons.map((lesson) => lesson.lessonGuid));
    setQuestionFilters((prev) => ({
      ...prev,
      unit: allUnitGuids,
      lesson: allLessonGuids,
    }));
  }

  function parseCourseItems(courseItems) {
    return courseItems.map((item) => {
      return {
        unitName: item.section || item.lesson_name,
        unitGuid: item.guid,
        lessons:
          item.items.length > 0
            ? item.items.map((lesson) => {
                return { lessonName: lesson.lesson_name, lessonGuid: lesson.lesson_guid };
              })
            : [],
      };
    });
  }

  function parseCourse(course, courseItems) {
    return {
      courseName: course.name || '',
      units: parseCourseItems(courseItems),
    };
  }

  function cleanLearningObjectives(lo) {
    return lo.filter((item) => item.parent_guid !== null);
  }

  function parseLoToSelectOptions(lo) {
    let _lo = cleanLearningObjectives(lo);
    return _lo.map((item) => ({ label: item.learning_objective, value: item.guid }));
  }

  function onSelectLearningObjective(lo) {
    if (lo?.value) {
      setLearningObjectives(learningObjectives.filter((item) => item.value !== lo.value));
      setSelectedLearningObjectives([...selectedLearningObjectives, lo]);
      let selectedLearningObjectivesGuids = [...selectedLearningObjectives, lo].map((item) => item.value);
      setQuestionFilters({ ...questionFilters, learningObjective: selectedLearningObjectivesGuids });
      setFiltersEmpty(false);
    }
  }

  function onRemoveLearningObjective(e, action) {
    const { removedValue: lo } = action;
    setLearningObjectives([...learningObjectives, lo]);
    setSelectedLearningObjectives(selectedLearningObjectives.filter((item) => item.value !== lo.value));
    let selectedLearningObjectivesGuids = selectedLearningObjectives.filter((item) => item.value !== lo.value).map((item) => item.value);
    setQuestionFilters({ ...questionFilters, learningObjective: selectedLearningObjectivesGuids });
    setFiltersEmpty(false);
  }

  function parseJSONData(data) {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }

  function getQuestionType(question) {
    let type = question.question_type_guid;
    if (!type) {
      try {
        let lemonadeType = getLemonadeType(question.question_data);
        if (lemonadeType === 'shorttext') return 'QT_17';
        if (lemonadeType === 'longtextV2') return 'QT_18';
        if (lemonadeType === 'uploadfile') return 'QT_19';
        if (lemonadeType === 'audiorecorder') return 'QT_23';
        if (lemonadeType === 'clozedropdown') return 'QT_28';
      } catch {
        // noop
      }
    }
    return type;
  }

  function parseQuestions(payload, offset) {
    const { data } = payload.data;
    let newQuestions = data.contents;
    let alreadyAddedGuids = alreadyAddedQuestions.map((item) => item.question_guid || item.content_guid);
    const validQuestions = newQuestions.filter((question) => question.question_data && Object.keys(parseJSONData(question.question_data)).length > 0);

    setQuestions({
      data: validQuestions.map((question) => ({
        ...question,
        disabled: alreadyAddedGuids.includes(question.guid),
        title: removeHtmlTagsFromString(question.question_stimulus),
        question_is_autoevaluative: question.question_is_autoevaluative === 1 ? 1 : questionIsAutoevaluative(question.question_data),
        image: `/images/templates/${getExerciseCover(getQuestionType(question))}.png`,
      })),
      left: data.left,
      offset: offset,
      total: data.total ?? 0,
      // Las preguntas sin question_data no se listan: se descuentan del contador visible (no del total del servidor, que gobierna la paginación)
      discardedCount: newQuestions.length - validQuestions.length,
      loading: false,
      pageSize: questions.pageSize,
      hasMore: data.left > 0,
    });
  }

  function onClose() {
    onCancel?.();
    onCleanFilters();
  }

  function onChangeCoursesTab(tab) {
    setCourseTabActive(tab);
  }

  function onChangeSelectedQuestions(guid) {
    if (selectedQuestions.some((question) => question.guid === guid)) {
      setSelectedQuestions(selectedQuestions.filter((question) => question.guid !== guid));
    } else {
      const newQuestionToSelect = questions.data.filter((question) => question.guid === guid);
      setSelectedQuestions([...newQuestionToSelect, ...selectedQuestions]);
    }
  }

  function onChangeSearch(search) {
    setQuestionsSearch(search);
  }

  function onChangeIsAutoevaluative(isAutoevaluative) {
    setQuestionFilters({ ...questionFilters, isAutoevaluative: isAutoevaluative });
    setFiltersEmpty(false);
  }

  function onSelectAuthor(e) {
    const author = e.target.value;
    // Las actividades de autoría docente no distinguen libro/adicionales: al pasar a docente se resetea el origen
    const source = author === QUESTION_AUTHOR.TEACHER ? ACTIVITY_SOURCE.ALL : questionFilters.source;
    setQuestionFilters({ ...questionFilters, author, source });
    setFiltersEmpty(false);
  }

  function onSelectSource(e) {
    setQuestionFilters({ ...questionFilters, source: e.target.value });
    setFiltersEmpty(false);
  }

  function onSelecteType(questionTypes) {
    setQuestionFilters({ ...questionFilters, type: questionTypes });
    setFiltersEmpty(false);
  }

  function onAddQuestions() {
    onAdd(selectedQuestions);
    onCleanFilters();
  }

  function onCleanFilters() {
    setQuestionFilters(FILTER_QUESTIONS_INITIAL_STATE);
    setSelectedLearningObjectives([]);
    setLearningObjectives(parseLoToSelectOptions(listOfAllLearningObjectives));
    setQuestionsSearch('');
    setFiltersEmpty(true);
    selectAllUnits(currentCourseItems);
  }

  function onClickRemoveSearch(search) {
    let recentSearchesNew = recentSearches.filter((item) => item !== search);
    setRecentSearches(recentSearchesNew);
    dispatch(coreEntities.history.actions.setRecentQuestionSearches(recentSearchesNew));
  }

  function onAddQuestionSearch(search) {
    if (recentSearches.includes(search)) return;
    let newSearches = recentSearches.length === 0 ? [search] : [search, ...recentSearches];
    setRecentSearches(newSearches);
    dispatch(coreEntities.history.actions.setRecentQuestionSearches(newSearches));
  }

  const onShowMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  // El backend solo aplica el filtro source cuando se filtra por unit/lesson (contrato TAN-8596)
  const hasCourseItemsFilter = questionFilters.unit.length > 0 || questionFilters.lesson.length > 0;
  const shouldShowActivitySourceFilter = questionFilters.author !== QUESTION_AUTHOR.TEACHER && hasCourseItemsFilter;

  return {
    questions: questions.data,
    selectedQuestions,
    recentSearches,
    search: questionsSearch,
    isAutoevaluative: questionFilters.isAutoevaluative,
    // discardedCount es de la página actual: solo es fiable restarlo cuando el resultado completo cabe en una página
    total: questions.total <= PAGE_SIZE ? Math.max(0, questions.total - questions.discardedCount) : questions.total,
    totalPages,
    hasMoreQuestions: questions.hasMore,
    filtersEmpty,
    questionFilters,
    isLoading,
    showMobileMenu,
    selectedPage,
    learningObjectives,
    selectedLearningObjectives,
    courseTabActive,
    currentCourseItems,
    edLevels,
    disciplines,
    onSelecteType,
    onPageChange,
    onGetQuestions,
    onChangeIsAutoevaluative,
    onChangeSearch,
    setSelectedPage,
    onShowMobileMenu,
    setQuestionFilters,
    onClose,
    onAddQuestions,
    onCleanFilters,
    onChangeCoursesTab,
    onSelectLearningObjective,
    onRemoveLearningObjective,
    onChangeSelectedQuestions,
    onSelectAuthor,
    shouldShowActivitySourceFilter,
    onSelectSource,
    onSelectFilter,
    onClickRemoveSearch,
    onAddQuestionSearch,
  };
}
